import { useEffect, useState } from "react";
import { Alert, Box, Button, Checkbox, FormControl, FormControlLabel, InputLabel, MenuItem, Select, TextField, Typography } from "@mui/material";

// An open-ended answer rule describes the SET of right answers, which no list of stored values can.
// "Write down a number between 0.3 and 2/5" has infinitely many; "two multiples of 2001" has
// infinitely many pairs; "a point the same distance from D as from C" is a whole line. The stored
// correct answer stays — it is what the reveal falls back on and what the AI grader reads — but a
// rule, once set, decides the verdict on its own.
const KINDS = [
  { value: "", label: "No rule — match the stored answer" },
  { value: "range", label: "Any value in a range" },
  { value: "multiple_of", label: "Any multiple of a number" },
  { value: "factor_of", label: "Any factor of a number" },
  { value: "member_of", label: "Any value from a list" },
  { value: "coordinate", label: "Coordinate with a free axis" },
  { value: "product", label: "Values that multiply to a total" },
  { value: "sum", label: "Values that add to a total" },
  { value: "derived", label: "Depends on an earlier part's answer" },
  { value: "congruence", label: "Congruence class (n ≡ r mod m)" },
  { value: "perfect_square", label: "Any square number" },
  { value: "prime", label: "Any prime number" },
  { value: "statistic", label: "Numbers with a given average / range" },
  { value: "attribute_variant", label: "An object differing from another in N ways" },
  { value: "follows_drawing", label: "Depends on the shape drawn in an earlier part" },
  { value: "qualified", label: "Shape name with a required qualifier" },
  { value: "relation", label: "Values related by an equation" },
];

// The written form the answer must take. A range alone cannot express "write down any FRACTION between
// 1/3 and 1/2": 0.4 sits inside the range and is still the wrong form. Nothing ticked = every form is
// accepted, which is how every rule authored before this behaved.
const FORMS = [
  { value: "fraction", label: "Fraction", hint: "5/12, 1 1/2, \\frac{5}{12}" },
  { value: "decimal", label: "Decimal", hint: "0.4 (a whole number counts too)" },
  { value: "whole", label: "Whole number", hint: "7" },
  { value: "percent", label: "Percentage", hint: "40%" },
];

const HELP = {
  range: 'Accepts any number strictly between the two values (tick "include the endpoints" for ≤ / ≥). Fractions, mixed numbers and LaTeX are all read as their value, so 7/20 passes a 0.3–0.4 range — tick a required form below when only a fraction (or only a decimal) will do.',
  multiple_of: 'Accepts any multiple of the number. Optional "ends with" digit (e.g. multiple of 12 ending in 2 → 12, 72, 132…). "Write down two multiples of 2001" — each box is checked, and the same value twice does not count twice.',
  factor_of: 'Accepts any factor of the number. The reveal lists them, so 2001 shows "1, 3, 23, 29, 69, 87, 667, 2001".',
  member_of: "Accepts any one of the values you list. Use this when the set is short and not a range or a multiple.",
  coordinate: 'Leave an axis blank (or type *) to accept any value there — "any point on the line x = 1".',
  product: "The values in the boxes must multiply to this total — a rectangle with the same area as a 24 cm square accepts 32×18, 24×24, 16×36 and every other pair.",
  sum: 'The values in the boxes must add to this total. Tick "one positive and one negative" when the question requires opposite signs (e.g. two numbers that add to 10).',
  derived: "The right answer is worked out from the answer the student gave to an earlier part of this same grouped question — e.g. the perimeter of whatever rectangle they drew.",
  congruence: 'Accepts any integer n where n ≡ residue (mod modulus). Example: modulus 60, residue 12 → 12, 72, 132, 192… (multiples of 4 and 6 that end in 2).',
  prime: 'Accepts any prime number (2, 3, 5, 7, 11…). List exceptions when the question says "another" one — the primes an earlier part already named are then the only wrong answers.',
  perfect_square: 'Accepts any square number (1, 4, 9, 16…). Tick "odd only" for "odd and square" — 9, 25, 49, 81, 121… all pass; 4 and 16 do not.',
  attribute_variant: '"Describe a tile which differs from the small white triangle in 1 way." Name the object being compared with and the properties it is described by, and every description differing from it in exactly that many properties is accepted — six right answers here, of which the mark scheme can only print one. Properties the student leaves unsaid are taken from the reference object, so "a square" is graded as the small white square.',
  follows_drawing: '"Plot D so ABCD has one line of symmetry", then "name ABCD": the answer depends on WHICH correct shape the student drew. One line per branch of the earlier drawing part — line 1 is its main target, line 2 onward its "also accept" arrangements in order; separate the names one branch accepts with "|". If the drawing cannot be read, every line is accepted.',
  qualified: "A shape name whose qualifier is the point of the question (\"equilateral triangle\"). Authored by the import tools; shown here so saving the question keeps it.",
  relation: '"a = 3, b = 6" — values that must satisfy an equation together. Authored by the import tools; shown here so saving the question keeps it.',
  statistic: '"Write down three numbers which have a range of 50 and a median of 17" — fill in only the statistics the question pins and any list with those properties passes (3, 17, 53 and 10, 17, 60 both do). The numbers may be typed into one box or one per box; leave a statistic blank when the question does not fix it.',
};

// Members are comma separated, but a member may itself hold commas — a coordinate pair "(3, 8)". A
// plain split turned three accepted points into six half-values, none of which any answer can match,
// so only commas OUTSIDE brackets separate.
const splitRuleValues = (text) => {
  const out = [];
  let depth = 0;
  let cur = "";
  for (const ch of String(text ?? "")) {
    if (ch === "(" || ch === "[") depth++;
    else if (ch === ")" || ch === "]") depth = Math.max(0, depth - 1);
    if (ch === "," && depth === 0) { out.push(cur); cur = ""; continue; }
    cur += ch;
  }
  out.push(cur);
  return out.map((v) => v.trim()).filter(Boolean);
};

// The parsed values are re-joined for display, so binding the field straight to them rewrote the box on
// every keystroke: the comma just typed was dropped as an empty member and the space after it trimmed
// away, which made "1, 17, 51" impossible to type (authors resorted to bracketing the whole list). Hold
// the raw text and only re-adopt the stored values when they stop describing what is typed.
const RuleValuesField = ({ values, onChange, ...props }) => {
  const joined = (values || []).join(", ");
  const [text, setText] = useState(joined);

  useEffect(() => {
    if (splitRuleValues(text).join("|") !== (values || []).join("|")) setText(joined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [joined]);

  return (
    <TextField
      {...props}
      value={text}
      onChange={(e) => {
        setText(e.target.value);
        onChange(splitRuleValues(e.target.value));
      }}
    />
  );
};

// attribute_variant's properties are an object ({ size: ["small", "large|big"] }) but authors think in
// rows, and an empty row has to survive typing its name before its words exist — so the rows are held
// here and only the filled ones are written back.
const AttributeRowsField = ({ attributes, onChange }) => {
  const [rows, setRows] = useState(() => {
    const entries = Object.entries(attributes || {});
    return entries.length ? entries.map(([name, words]) => ({ name, words: (words || []).join(", ") })) : [{ name: "", words: "" }];
  });

  const push = (next) => {
    setRows(next);
    const out = {};
    next.forEach((r) => {
      const name = r.name.trim();
      const words = splitRuleValues(r.words);
      if (name && words.length) out[name] = words;
    });
    onChange(out);
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Typography variant="caption" sx={{ color: "#555", fontWeight: 700, display: "block", mb: 0.25 }}>
        Properties the object is described by
      </Typography>
      <Typography variant="caption" sx={{ color: "#777", display: "block", mb: 0.75 }}>
        One row per property. Words are comma separated; write synonyms of one value together with "|"
        (large|big). Every value the question allows must appear, or a right answer naming it is missed.
      </Typography>
      {rows.map((r, i) => (
        <Box key={i} sx={{ display: "flex", gap: 1, mb: 0.75, alignItems: "center" }}>
          <TextField
            size="small"
            label="Property"
            value={r.name}
            onChange={(e) => push(rows.map((x, xi) => (xi === i ? { ...x, name: e.target.value } : x)))}
            placeholder="colour"
            sx={{ width: 160 }}
          />
          <TextField
            size="small"
            fullWidth
            label="Values"
            value={r.words}
            onChange={(e) => push(rows.map((x, xi) => (xi === i ? { ...x, words: e.target.value } : x)))}
            placeholder="black, grey|gray, white"
          />
          <Button
            size="small"
            onClick={() => push(rows.length > 1 ? rows.filter((_, xi) => xi !== i) : [{ name: "", words: "" }])}
          >
            Remove
          </Button>
        </Box>
      ))}
      <Button size="small" onClick={() => push([...rows, { name: "", words: "" }])}>+ Add property</Button>
    </Box>
  );
};

const AnswerRuleEditor = ({ rule, setRule, blankCount = 0 }) => {
  const value = rule && typeof rule === "object" ? rule : {};
  const kind = value.kind || "";

  const patch = (changes) => setRule({ ...value, ...changes });

  const setKind = (next) => {
    if (!next) return setRule(null);
    // Carrying a "min" over to a multiple_of rule stores a field that kind never reads and that the
    // author cannot see — start each kind from a clean object, keeping only the shared toggles.
    setRule({
      kind: next,
      distinct: value.distinct !== false,
      integers_only: !!value.integers_only,
      forms: Array.isArray(value.forms) ? value.forms : [],
      display: value.display || "",
    });
  };

  const forms = Array.isArray(value.forms) ? value.forms : [];
  // Kept in FORMS order, not tick order — the reveal reads the list out ("Any fraction or decimal …").
  const toggleForm = (form, on) =>
    patch({
      forms: FORMS.map((f) => f.value).filter((v) => (v === form ? on : forms.includes(v))),
    });

  const numField = (name, label, placeholder, width = 140) => (
    <TextField
      size="small"
      label={label}
      value={value[name] ?? ""}
      onChange={(e) => patch({ [name]: e.target.value })}
      placeholder={placeholder}
      sx={{ width }}
    />
  );

  return (
    <Box sx={{ mt: 2, p: 1.5, border: "1px dashed #bbb", borderRadius: 1 }}>
      <Typography variant="caption" sx={{ fontWeight: 700, color: "#555", display: "block", mb: 0.5 }}>
        Open-ended answer rule (optional)
      </Typography>
      <Typography variant="caption" sx={{ color: "#777", display: "block", mb: 1 }}>
        Use this when the question has too many right answers to list — "any number between…", "any two
        multiples of…", "any point on this line". Leave it on "No rule" for ordinary questions.
      </Typography>

      <FormControl size="small" sx={{ minWidth: 290 }}>
        <InputLabel>Answer rule</InputLabel>
        <Select label="Answer rule" value={kind} onChange={(e) => setKind(e.target.value)}>
          {KINDS.map((k) => (
            <MenuItem key={k.value} value={k.value}>{k.label}</MenuItem>
          ))}
        </Select>
      </FormControl>

      {!!kind && (
        <>
          <Alert severity="info" sx={{ py: 0, mt: 1, mb: 1 }}>
            <Typography variant="caption">
              {HELP[kind]} While a rule is set it decides the verdict on its own — the stored answer and
              the AI grader are not consulted.
            </Typography>
          </Alert>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 1 }}>
            {kind === "range" && (
              <>
                {numField("min", "From", "0.3")}
                {numField("max", "To", "0.4")}
                <FormControlLabel
                  sx={{ m: 0 }}
                  control={
                    <Checkbox
                      size="small"
                      checked={!!value.inclusive}
                      onChange={(e) => patch({ inclusive: e.target.checked })}
                    />
                  }
                  label={<Typography variant="caption">Include the endpoints</Typography>}
                />
              </>
            )}

            {(kind === "multiple_of" || kind === "factor_of") && (
              <>
                {numField("of", kind === "multiple_of" ? "Multiple of" : "Factor of", "2001")}
                {kind === "multiple_of" && numField("ends_with", "Ends with (optional)", "2", 120)}
                <FormControlLabel
                  sx={{ m: 0 }}
                  control={
                    <Checkbox
                      size="small"
                      checked={value.allow_trivial !== false}
                      onChange={(e) => patch({ allow_trivial: e.target.checked })}
                    />
                  }
                  label={
                    <Typography variant="caption">
                      {kind === "multiple_of" ? "Allow the number itself" : "Allow 1 and the number itself"}
                    </Typography>
                  }
                />
              </>
            )}

            {kind === "congruence" && (
              <>
                {numField("modulus", "Modulus", "60")}
                {numField("residue", "Residue", "12")}
              </>
            )}

            {kind === "perfect_square" && (
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={!!value.odd}
                    onChange={(e) => patch({ odd: e.target.checked })}
                  />
                }
                label={<Typography variant="caption">Odd numbers only</Typography>}
              />
            )}

            {kind === "prime" && (
              <RuleValuesField
                size="small"
                fullWidth
                label="Except these (comma separated, optional)"
                values={value.exclude}
                onChange={(exclude) => patch({ exclude })}
                placeholder="13, 37"
              />
            )}

            {kind === "member_of" && (
              <RuleValuesField
                size="small"
                fullWidth
                label="Accepted values (comma separated)"
                values={value.values}
                onChange={(values) => patch({ values })}
                placeholder="1, 3, 23, 29, 69, 87, 667, 2001"
              />
            )}

            {kind === "statistic" && (
              <>
                {numField("count", "How many numbers", "3", 150)}
                {numField("range", "Range", "50", 110)}
                {numField("median", "Median", "17", 110)}
                {numField("mean", "Mean", "", 110)}
                {numField("mode", "Mode", "", 110)}
              </>
            )}

            {kind === "attribute_variant" && (
              <>
                <TextField
                  size="small"
                  label="Differs from"
                  value={value.reference ?? ""}
                  onChange={(e) => patch({ reference: e.target.value })}
                  placeholder="small white triangle"
                  helperText="Must name a value of every property below"
                  sx={{ width: 260 }}
                />
                {numField("differ_by", "In how many ways", "1", 150)}
                <AttributeRowsField
                  attributes={value.attributes}
                  onChange={(attributes) => patch({ attributes })}
                />
              </>
            )}

            {kind === "coordinate" && (
              <>
                {numField("x", "x must be", "1 or *")}
                {numField("y", "y must be", "* for any")}
              </>
            )}

            {(kind === "product" || kind === "sum") && (
              <>
                {numField("value", kind === "product" ? "Product" : "Total", "576")}
                {kind === "sum" && (
                  <FormControlLabel
                    sx={{ m: 0 }}
                    control={
                      <Checkbox
                        size="small"
                        checked={!!value.opposite_signs}
                        onChange={(e) => patch({ opposite_signs: e.target.checked })}
                      />
                    }
                    label={<Typography variant="caption">One positive and one negative</Typography>}
                  />
                )}
                {blankCount < 2 && (
                  <Typography variant="caption" sx={{ color: "#b26a00" }}>
                    Needs at least two answer boxes (or values joined by "and" / "+" in one box).
                  </Typography>
                )}
              </>
            )}

            {kind === "follows_drawing" && (
              <>
                <TextField
                  size="small"
                  label="Earlier drawing part (text to look for)"
                  value={value.source ?? ""}
                  onChange={(e) => patch({ source: e.target.value })}
                  placeholder="Plot another point, D"
                  sx={{ width: 280 }}
                />
                <TextField
                  size="small"
                  multiline
                  minRows={2}
                  label="Answer for each branch (one per line)"
                  value={(Array.isArray(value.branch_answers) ? value.branch_answers : [])
                    .map((b) => (Array.isArray(b) ? b : [b]).join(" | ")).join("\n")}
                  onChange={(e) => patch({
                    branch_answers: e.target.value.split("\n")
                      .map((line) => line.split("|").map((v) => v.trim()).filter(Boolean)),
                  })}
                  placeholder={"Isosceles trapezium | Trapezium\nKite"}
                  sx={{ width: 320 }}
                />
              </>
            )}

            {kind === "derived" && (
              <>
                <TextField
                  size="small"
                  label="Earlier part (text to look for)"
                  value={value.source ?? ""}
                  onChange={(e) => patch({ source: e.target.value })}
                  placeholder="sketch of a rectangle"
                  sx={{ width: 280 }}
                />
                <TextField
                  size="small"
                  label="Worked out as"
                  value={value.expression ?? ""}
                  onChange={(e) => patch({ expression: e.target.value })}
                  placeholder="2*(a+b)"
                  helperText="a, b, c… are that part's answer boxes, left to right"
                  sx={{ width: 220 }}
                />
              </>
            )}
          </Box>

          {!["coordinate", "derived", "statistic", "attribute_variant", "follows_drawing", "qualified", "relation"].includes(kind) && (
            <Box sx={{ mb: 0.5 }}>
              <Typography variant="caption" sx={{ color: "#555", fontWeight: 700, display: "block" }}>
                The answer must be written as
              </Typography>
              <Typography variant="caption" sx={{ color: "#777", display: "block", mb: 0.25 }}>
                Tick one or several — "write down any fraction between ⅓ and ½" accepts 5/12 but not 0.4.
                Leave them all clear to accept any form.
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                {FORMS.map((f) => (
                  <FormControlLabel
                    key={f.value}
                    sx={{ m: 0 }}
                    control={
                      <Checkbox
                        size="small"
                        checked={forms.includes(f.value)}
                        onChange={(e) => toggleForm(f.value, e.target.checked)}
                      />
                    }
                    label={
                      <Typography variant="caption">
                        {f.label} <Typography component="span" variant="caption" sx={{ color: "#999" }}>({f.hint})</Typography>
                      </Typography>
                    }
                  />
                ))}
              </Box>
            </Box>
          )}

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
            {kind !== "coordinate" && kind !== "derived" && kind !== "attribute_variant" && (
              <FormControlLabel
                sx={{ m: 0 }}
                control={
                  <Checkbox
                    size="small"
                    checked={!!value.integers_only}
                    onChange={(e) => patch({ integers_only: e.target.checked })}
                  />
                }
                label={<Typography variant="caption">Whole numbers only</Typography>}
              />
            )}
            {blankCount > 1 && !["coordinate", "derived", "product", "sum", "statistic", "attribute_variant"].includes(kind) && (
              <FormControlLabel
                sx={{ m: 0 }}
                control={
                  <Checkbox
                    size="small"
                    checked={value.distinct !== false}
                    onChange={(e) => patch({ distinct: e.target.checked })}
                  />
                }
                label={<Typography variant="caption">The boxes must hold different values</Typography>}
              />
            )}
          </Box>

          <TextField
            size="small"
            fullWidth
            label="What to show as the correct answer (optional)"
            value={value.display ?? ""}
            onChange={(e) => patch({ display: e.target.value })}
            placeholder="Any decimal or fraction between 0.3 and 0.4"
            helperText="Shown only after the student answers. Leave blank to let the rule describe itself."
            sx={{ mt: 1 }}
          />
        </>
      )}
    </Box>
  );
};

export default AnswerRuleEditor;
